import { CloseRounded } from "@mui/icons-material";
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  Dialog,
  Divider,
  IconButton,
  TextField as MuiTextField,
  Typography,
  styled,
} from "@mui/material";
import { useQuery } from "react-query";
import {
  getCityMasterData,
  getDistrictMasterData,
  getStateMasterData,
} from "../../../../services/master/master_service";
import { getJobApplicationFilters } from "../../../../services/jobs/jobs";
import { useJobApplicationFilterStore } from "../../../../store/use-job-application-filter-store";
import { DatePicker } from "@mui/x-date-pickers";
import dayjs, { Dayjs } from "dayjs";
import { useEffect, useMemo, useState } from "react";
import { black, greyEnabled, purple, purpleLight } from "../../../../theme/colors";

interface FilterServicesInterface {
  openModal: boolean;
  setOpenModal: (val: boolean) => void;
}

const TextField = styled(MuiTextField)(() => ({
  "& .MuiOutlinedInput-root": {
    backgroundColor: "#FFFFFF",
    borderRadius: 8,
    "& fieldset": { borderColor: "#ABA0AB" },
    "&:hover fieldset": { borderColor: purple },
    "&.Mui-focused fieldset": { borderColor: purple, borderWidth: 2 },
  },
}));

export default function FilterJobApplication({
  openModal,
  setOpenModal,
}: FilterServicesInterface) {
  const {
    stateId,
    cityId,
    districtId,
    categoryId,
    subCategoryId,
    selectedStatus,
    startDate,
    endDate,
    ageFrom,
    ageTo,
    gender,
    setStateId,
    setCityId,
    setDistrictId,
    setCategoryId,
    setSubCategoryId,
    setSelectedStatus,
    setStartDate,
    setEndDate,
    setAgeFrom,
    setAgeTo,
    setGender,
    resetFilters,
  } = useJobApplicationFilterStore();

  const [_stateId, _setStateId] = useState<number | null>(stateId);
  const [_city, _setCity] = useState<number | null>(cityId);
  const [_district, _setDistrict] = useState<number | null>(districtId);
  const [_categoryId, _setCategoryId] = useState<number | null>(categoryId);
  const [_subCategoryId, _setSubCategoryId] = useState<number | null>(
    subCategoryId
  );
  const [_selectedStatus, _setSelectedStatus] = useState<number | null>(selectedStatus);
  const [_endDate, _setEndDate] = useState<string>(endDate);
  const [_startDate, _setStartDate] = useState<string>(startDate);
  const [_ageFrom, _setAgeFrom] = useState<number | null>(ageFrom);
  const [_ageTo, _setAgeTo] = useState<number | null>(ageTo);
  const [_selectedGender, _setSelectedGender] = useState<string>(gender);

  function resetDraftFromStore() {
    _setStateId(stateId);
    _setCity(cityId);
    _setDistrict(districtId);
    _setCategoryId(categoryId);
    _setSubCategoryId(subCategoryId);
    _setSelectedStatus(selectedStatus);
    _setStartDate(startDate);
    _setEndDate(endDate);
    _setAgeFrom(ageFrom);
    _setAgeTo(ageTo);
    _setSelectedGender(gender);
  }

  const { data: filterList } = useQuery(["jobApplicationFilters"], async () => {
    let response = await getJobApplicationFilters();
    return response?.data?.data;
  });

  const selectedCategory = useMemo(() => {
    return filterList?.categories?.find(
      (category: any) => category?.id === _categoryId
    );
  }, [filterList?.categories, _categoryId]);

  const selectedCategorySubcategories = useMemo(() => {
    return selectedCategory?.subcategories || [];
  }, [selectedCategory]);

  const { data: states } = useQuery(["master-Data-List-state"], async () => {
    let response = await getStateMasterData();
    return response?.data?.data?.list;
  });

  const { data: city } = useQuery(["cityList", _stateId], async () => {
    if (_stateId) {
      let response = await getCityMasterData(_stateId);
      return response?.data?.data?.list;
    }
  });
  const { data: district } = useQuery(["districtList", _stateId], async () => {
    if (_stateId) {
      let response = await getDistrictMasterData(_stateId);
      return response?.data?.data?.list;
    }
  });

  useEffect(() => {
    if (!selectedCategorySubcategories?.length) {
      _setSubCategoryId(null);
      return;
    }

    const isValidSubCategory = selectedCategorySubcategories?.some(
      (subcategory: any) => subcategory?.id === _subCategoryId
    );

    if (!isValidSubCategory) {
      _setSubCategoryId(null);
    }
  }, [selectedCategorySubcategories, _subCategoryId]);

  function _dropdownOption({
    title,
    placeholder,
    options,
    selectedId,
    onSelect,
  }: {
    title: string;
    placeholder: string;
    options: { id: number; value: string }[] | undefined;
    selectedId: number | null;
    onSelect: (id: number | null) => void;
  }) {
    return (
      <Box mb={2}>
        <Typography variant="body2" fontWeight={600} color={black} mb={0.5}>
          {title}
        </Typography>
        <Autocomplete
          fullWidth
          size="small"
          value={
            (Array.isArray(options)
              ? options.find((option) => selectedId === option?.id)
              : undefined) || null
          }
          onChange={(e, value) => {
            onSelect(value?.id || null);
          }}
          options={Array.isArray(options) ? options : []}
          getOptionLabel={(option) => option?.value || ""}
          renderInput={(params) => <TextField {...params} placeholder={placeholder} />}
        />
      </Box>
    );
  }

  function _chipGroup({
    title,
    options,
    selectedValue,
    onSelect,
  }: {
    title: string;
    options: { id?: number; value: string }[] | undefined;
    selectedValue: string | number | null;
    onSelect: (value: any) => void;
  }) {
    if (!options?.length) return null;
    return (
      <Box mb={2}>
        <Typography variant="body2" fontWeight={600} color={black} mb={0.5}>
          {title}
        </Typography>
        <Box display="flex" flexWrap="wrap" gap={1}>
          {options.map((option) => {
            const optionValue = option.id ?? option.value;
            const selected = selectedValue === optionValue;
            return (
              <Chip
                key={option.value}
                label={option.value}
                clickable
                variant={selected ? "filled" : "outlined"}
                color="primary"
                onClick={() => onSelect(selected ? null : optionValue)}
              />
            );
          })}
        </Box>
      </Box>
    );
  }

  const handleClose = () => {
    resetDraftFromStore();
    setOpenModal(false);
  };

  return (
    <Dialog open={openModal} onClose={handleClose} PaperProps={{ sx: { borderRadius: 4 } }}>
      <Box sx={{ width: 440, maxWidth: "90vw" }}>
        <Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          sx={{ px: 3, pt: 2.5, pb: 1.5, borderBottom: "1px solid #ECECEC" }}
        >
          <Typography variant="h6" fontWeight={700} color={black}>
            Filter Job Applications
          </Typography>
          <IconButton onClick={handleClose} size="small">
            <CloseRounded sx={{ color: greyEnabled }} />
          </IconButton>
        </Box>

        <Box sx={{ px: 3, pt: 2, pb: 3, maxHeight: "70vh", overflow: "auto" }}>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="body2" fontWeight={600} color={black}>
              Filter By
            </Typography>
            <Button
              variant="text"
              sx={{ p: 0, minWidth: "auto", color: purple, textTransform: "none", fontWeight: 600 }}
              onClick={() => {
                resetFilters();
                _setStateId(null);
                _setSelectedStatus(null);
                _setCity(null);
                _setDistrict(null);
                _setCategoryId(null);
                _setSubCategoryId(null);
                setOpenModal(false);
                _setStartDate("");
                _setEndDate("");
                _setAgeFrom(null);
                _setAgeTo(null);
                _setSelectedGender("");
              }}
            >
              Reset
            </Button>
          </Box>
          <Divider sx={{ mt: 1.5, mb: 2 }} />

          {_dropdownOption({
            title: "Category",
            placeholder: "Search for Category",
            options: filterList?.categories || [],
            selectedId: _categoryId,
            onSelect: (id) => {
              _setCategoryId(id);
              _setSubCategoryId(null);
            },
          })}
          {selectedCategorySubcategories?.length > 0 &&
            _dropdownOption({
              title: "Sub Category",
              placeholder: "Search for Sub Category",
              options: selectedCategorySubcategories,
              selectedId: _subCategoryId,
              onSelect: (id) => {
                _setSubCategoryId(id);
              },
            })}
          {_dropdownOption({
            title: "State",
            placeholder: "Search for State",
            options: states || [],
            selectedId: _stateId,
            onSelect: (id) => {
              _setStateId(id);
              _setCity(null);
              _setDistrict(null);
            },
          })}
          {_dropdownOption({
            title: "District",
            placeholder: "Search for District",
            options: district,
            selectedId: _district,
            onSelect: (id) => {
              _setDistrict(id);
            },
          })}
          {_dropdownOption({
            title: "City",
            placeholder: "Search for City",
            options: city,
            selectedId: _city,
            onSelect: (id) => {
              _setCity(id);
            },
          })}

          <Typography variant="body2" fontWeight={600} color={black} mb={1}>
            Date Range
          </Typography>
          <Box display="flex" gap={1.5} mb={2}>
            <DatePicker
              label="From Date"
              format="DD-MM-YYYY"
              disableFuture
              value={_startDate ? dayjs(_startDate, "YYYY-MM-DD") : null}
              onChange={(value: Dayjs | null) => {
                _setStartDate(value?.format("YYYY-MM-DD") || "");
              }}
              slotProps={{ textField: { size: "small" } }}
              sx={{ "& .MuiSvgIcon-root": { fill: purple } }}
            />
            <DatePicker
              label="To Date"
              format="DD-MM-YYYY"
              disableFuture
              minDate={_startDate ? dayjs(_startDate, "YYYY-MM-DD") : undefined}
              value={_endDate ? dayjs(_endDate, "YYYY-MM-DD") : null}
              onChange={(value: Dayjs | null) => {
                _setEndDate(value?.format("YYYY-MM-DD") || "");
              }}
              slotProps={{ textField: { size: "small" } }}
              sx={{ "& .MuiSvgIcon-root": { fill: purple } }}
            />
          </Box>

          {_chipGroup({
            title: "Gender",
            options: filterList?.gender || [],
            selectedValue: _selectedGender,
            onSelect: (value) => _setSelectedGender(value || ""),
          })}
          {_chipGroup({
            title: "Status",
            options: filterList?.status || [],
            selectedValue: _selectedStatus,
            onSelect: (id) => _setSelectedStatus(id),
          })}

          <Box display="flex" gap={1.5} mt={2}>
            <Button
              fullWidth
              onClick={handleClose}
              variant="outlined"
              sx={{
                borderColor: purple,
                color: purple,
                borderRadius: 2,
                textTransform: "none",
                fontWeight: 600,
                "&:hover": { borderColor: purple, bgcolor: purpleLight },
              }}
            >
              Cancel
            </Button>
            <Button
              fullWidth
              variant="contained"
              disableElevation
              onClick={() => {
                setCityId(_city);
                setDistrictId(_district);
                setStateId(_stateId);
                setCategoryId(_categoryId);
                setSubCategoryId(_subCategoryId);
                setSelectedStatus(_selectedStatus);
                setStartDate(_startDate);
                setEndDate(_endDate);
                setAgeFrom(_ageFrom);
                setAgeTo(_ageTo);
                setGender(_selectedGender);
                setOpenModal(false);
              }}
              sx={{
                bgcolor: purple,
                borderRadius: 2,
                textTransform: "none",
                fontWeight: 600,
                "&:hover": { bgcolor: "#7A2074" },
              }}
            >
              Apply Filters
            </Button>
          </Box>
        </Box>
      </Box>
    </Dialog>
  );
}
